/**
 * Date range picker used on the events calendar
 */
import React, { useEffect, useRef, useState } from "react";
// vendor css is imported in day-picker.scss
import { DayPicker } from "react-day-picker";

import { mountReactComponent } from "../utilities";
import ClickAwayListener from "./ClickAwayListener";

function parseDate(dateIso) {
    if (!dateIso) return null;

    return new Date(`${ dateIso } 00:00:00`);
}

// Format in local time — toISOString would shift local midnight to the previous day for UTC+ timezones
function formatDate(date) {
    if (!date) return '';

    return `${ date.getFullYear() }-${ String(date.getMonth() + 1).padStart(2, '0') }-${ String(date.getDate()).padStart(2, '0') }`;
}

function formatDaterange(from, to) {
    if (!from && !to) return '';

    const formatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    if (!to) {
        return formatter.format(from) + '–';
    }

    return formatter.formatRange(from, to);
}

const Icon = ({ className, icon, orientation }) => {
    if (!icon) return;

    const angles = {
        up: '180',
        right: '270',
        down: '0',
        left: '90',
    }
    const rotation = angles[orientation];

    return (
        <span
            className={ className }
            style={ { display: 'flex', alignItems: 'center', transform: `rotate(${ rotation }deg)` } }
            dangerouslySetInnerHTML={ { __html: decodeURIComponent(icon) } }
        />
    );
}

function DayPickerWrapper({
                              datesWithEvents: datesWithEventsRaw,
                              icon,
                              initialDateFrom,
                              initialDateTo,
                              noEvents,
                              rootElement
                          }) {
    const [open, setOpen] = useState(false);
    const defaultDateFrom = parseDate(initialDateFrom);
    const defaultDateTo = parseDate(initialDateTo);
    const [selected, setSelected] = useState({
        from: defaultDateFrom,
        to: defaultDateTo,
    });
    const { from, to } = selected || {};
    const toggleRef = useRef();
    const dateRangeText = formatDaterange(from, to);
    const [datesWithEvents, setDatesWithEvents] = useState(JSON.parse(datesWithEventsRaw));
    const [loadedAllDatesWithEvents, setloadedAllDatesWithEvents] = useState(false);

    const calculateNumberOfMonths = () => {
        if (window.isMedium || window.isSmall) {
            return 1;
        } else {
            return 2;
        }
    }
    const [numberOfMonths, setNumberOfMonths] = useState(calculateNumberOfMonths());

    useEffect(() => {
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleResize = () => {
        setNumberOfMonths(calculateNumberOfMonths());
    };

    const handleClickButton = () => {
        setOpen(!open);
    };

    const handleClickAllDates = () => {
        setSelected({ from: '', to: '' });

        // this will probably not be necessary if I refactor the whole Calendar to no longer use the lists feature
        setTimeout(() => {
            rootElement.closest('form').requestSubmit();
        }, 0);
    };

    const isDayDisabled = (day) => (
        !datesWithEvents.includes(formatDate(day))
    );


    const handleSelect = (selectedDate) => {
        setSelected(selectedDate);
    };

    const handleSubmit = () => {
        rootElement.closest('form').requestSubmit();
        setOpen(false);
    };

    const handleClickAway = () => {
        setOpen(false);
    };

    const queryDaysWithEvents = async (e) => {
        // Query all events if user tries to navigate to other months
        // For optimization, this component only loads 3 months before queried date range on load
        if (loadedAllDatesWithEvents) return;

        try {
            const response = await fetch(`/events/days-with-events${ location.search }`);
            if (!response.ok) {
                throw new Error(`Failed to load days with events: ${ response.status }`);
            }
            const data = await response.json();

            // Only replace with an actual array so isDayDisabled can't crash on an error body
            if (Array.isArray(data)) {
                setDatesWithEvents(data);
                setloadedAllDatesWithEvents(true);
            }
        } catch (error) {
            console.error(error);
        }
    }

    const classNames = ['day-picker-popup'];
    if (open) {
        classNames.push('open');
    }

    const allDatesText = 'All dates';
    const pickDateText = <>Pick a date <Icon icon={ icon } orientation={ open ? 'up' : 'down' } className="ml-t"/></>;

    return (
        <>
            <div className="day-picker-buttons">
                <button
                    className={ `day-picker-button${ (!from && !to && !open) ? ' active' : '' }` }
                    type="button"
                    disabled={ noEvents === 'true' }
                    onClick={ handleClickAllDates }
                >
                    <span className="day-picker-button__text">{ allDatesText }</span>
                    <span className="day-picker-button__spacer">{ allDatesText }</span>
                </button>
                <button
                    className={ `day-picker-button${ (from || to || open) ? ' active' : '' }` }
                    type="button"
                    disabled={ noEvents === 'true' }
                    onClick={ handleClickButton }
                    ref={ toggleRef }
                >
                    <span className="day-picker-button__text">{ pickDateText }</span>
                    <span className="day-picker-button__spacer">{ pickDateText }</span>
                </button>

                { from && <input type="hidden" name="date_from" id="date_from" value={ formatDate(from) }/> }
                { to && <input type="hidden" name="date_to" id="date_to" value={ formatDate(to) }/> }
            </div>
            <ClickAwayListener
                onClickAway={ handleClickAway }
                buttonRef={ toggleRef }
                className={ classNames.join(' ') }
            >
                <DayPicker
                    components={ { Chevron: (props) => <Icon icon={ icon } { ...props } /> } }
                    defaultMonth={ defaultDateFrom || new Date() }
                    disabled={ isDayDisabled }
                    formatters={ { formatWeekdayName: (day) => day.toLocaleDateString('en-US', { weekday: 'narrow' }) } }
                    mode="range"
                    numberOfMonths={ numberOfMonths }
                    onMonthChange={ queryDaysWithEvents }
                    onSelect={ handleSelect }
                    selected={ selected }
                />

                { (from || to) && (
                    <>
                        <div className="mt-m body-large day-picker-selected">
                            <strong>Selected:</strong> { dateRangeText }</div>

                        <button className="btn btn--strong btn--stretch day-picker-submit" type="button"
                                onClick={ handleSubmit }>GO
                        </button>
                    </>
                ) }

            </ClickAwayListener>
        </>
    );
}

const observer = new MutationObserver((mutationsList, _observer) => {
    for (const mutation of mutationsList) {
        mutation.addedNodes.forEach(node => {
            if (node.classList?.contains('day-picker-frame')) {
                mountDayPicker();
            }
        });
    }
});

function mountDayPicker() {
    document.querySelectorAll('.day-picker').forEach((rootElement) => {
        mountReactComponent(
            rootElement,
            <DayPickerWrapper { ...rootElement.dataset } rootElement={ rootElement }/>
        );
    });
}

export default function setupDayPickers() {
    const element = document.querySelector('.events-form');

    if (!element) return;

    mountDayPicker();

    // Re-mount the component if Turbo Streams replaces it with new props
    observer.observe(element, { childList: true, subtree: true });
}
